/**
 * EvaluationsQueuePage.js
 * Cross-project evaluation sessions queue — shows sessions needing attention.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ExamPageShell from '../components/layout/ExamPageShell';
import EmptyState from '../components/common/EmptyState';
import ExamBadge from '../components/common/ExamBadge';
import { listExamProjects, listEvaluations } from '../api/examApi';

const SESSION_STATUS_META = {
  pending:        { label: 'Pending',        status: 'pending'  },
  evaluating:     { label: 'Evaluating',     status: 'pending'  },
  faculty_review: { label: 'Needs Review',   status: 'pending'  },
  completed:      { label: 'Completed',      status: 'ready'    },
  published:      { label: 'Published',      status: 'ready'    },
};

function SessionRow({ session, projectId, projectName }) {
  const meta = SESSION_STATUS_META[session.status] || { label: session.status, status: 'draft' };
  const needsAction = ['pending', 'evaluating', 'faculty_review'].includes(session.status);
  const total = session.student_count || 0;
  const done  = session.evaluated_count || 0;
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="exam-card" style={{
      padding: '14px 18px',
      borderLeft: `3px solid ${needsAction ? 'var(--exam-warning)' : 'var(--exam-success)'}`,
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--exam-text)' }}>
            {session.title || `Session #${session.id}`}
          </span>
          <ExamBadge label={meta.label} status={meta.status} />
          <span style={{ fontSize: 11, color: 'var(--exam-text-muted)' }}>
            — {projectName}
          </span>
        </div>
        {total > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, maxWidth: 160, height: 4, background: 'var(--exam-border)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? 'var(--exam-success)' : 'var(--exam-primary)' }} />
            </div>
            <span style={{ fontSize: 11, color: 'var(--exam-text-muted)' }}>{done}/{total} evaluated</span>
          </div>
        )}
      </div>
      <Link
        to={`/examination/projects/${projectId}/evaluation`}
        className="exam-btn exam-btn-primary exam-btn-sm"
        style={{ textDecoration: 'none', flexShrink: 0 }}
      >
        {needsAction ? 'Continue →' : 'View →'}
      </Link>
    </div>
  );
}

export default function EvaluationsQueuePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    listExamProjects()
      .then(async (data) => {
        const projects = Array.isArray(data) ? data : (data.projects || []);
        projects.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const results = await Promise.allSettled(
          projects.map((p) =>
            listEvaluations(p.id)
              .then((sessions) => (Array.isArray(sessions) ? sessions : []).map((s) => ({ ...s, projectId: p.id, projectName: p.title || p.name })))
              .catch(() => [])
          )
        );
        const all = results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
        setItems(all);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((s) => {
    if (filter === 'active') return ['pending', 'evaluating', 'faculty_review'].includes(s.status);
    if (filter === 'completed') return ['completed', 'published'].includes(s.status);
    return true;
  });

  const activeCount    = items.filter((s) => ['pending', 'evaluating', 'faculty_review'].includes(s.status)).length;
  const completedCount = items.filter((s) => ['completed', 'published'].includes(s.status)).length;

  return (
    <ExamPageShell breadcrumbs={[{ label: 'Evaluations' }]}>
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--exam-text)' }}>Evaluations Queue</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--exam-text-muted)' }}>
            All evaluation sessions across projects. Sessions needing attention are highlighted.
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Active / Pending', value: activeCount, color: 'var(--exam-warning)' },
            { label: 'Completed', value: completedCount, color: 'var(--exam-success)' },
            { label: 'Total Sessions', value: items.length, color: 'var(--exam-primary)' },
          ].map((s) => (
            <div key={s.label} className="exam-card" style={{ padding: '12px 20px', textAlign: 'center', minWidth: 120 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--exam-text-muted)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[{ key: 'active', label: 'Active' }, { key: 'completed', label: 'Completed' }, { key: 'all', label: 'All' }].map(({ key, label }) => (
            <button key={key} className={`exam-btn exam-btn-sm ${filter === key ? 'exam-btn-primary' : 'exam-btn-ghost'}`} onClick={() => setFilter(key)}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="exam-spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="📋" title={filter === 'active' ? 'No active evaluations' : 'No sessions found'} description="" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map((s) => (
              <SessionRow key={`${s.projectId}-${s.id}`} session={s} projectId={s.projectId} projectName={s.projectName} />
            ))}
          </div>
        )}
      </div>
    </ExamPageShell>
  );
}
